import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { PermissionNode, PermissionVersionLog, SysRole } from '@/core';
import { configurePermissionContext, isEndAllowed, type DataScope } from '@/core';
import * as roleApi from '@/api/role';
import { useAuthStore } from './auth';

const CURRENT_END = (import.meta.env.VITE_APP_END || 'ADMIN') as 'ADMIN' | 'BPO' | 'MINIAPP';

/** permission store — 菜单/按钮权限树、数据范围、角色权限版本与回滚 */
export const usePermissionStore = defineStore('permission', () => {
  const auth = useAuthStore();
  const roles = ref<SysRole[]>([]);
  const tree = ref<PermissionNode[]>([]);
  const versions = ref<PermissionVersionLog[]>([]);
  const loading = ref(false);

  const granted = computed(() => new Set(auth.permissions));
  const dataScope = computed<DataScope | undefined>(() => auth.user?.dataScope);

  /** 端绑定互斥：外部角色仅绑专属端，登录态与路由守卫均按端校验 */
  const endAllowed = computed(() => {
    const binding = auth.user?.endBinding ?? [];
    return binding.length > 0 ? isEndAllowed(CURRENT_END, binding) : false;
  });

  async function loadStaticData(): Promise<void> {
    loading.value = true;
    try {
      const [roleList, treeData] = await Promise.all([roleApi.fetchRoles(), roleApi.fetchPermissionTree()]);
      roles.value = roleList;
      tree.value = treeData;
    } finally {
      loading.value = false;
    }
  }

  async function loadVersions(roleId: string): Promise<void> {
    versions.value = await roleApi.fetchPermissionVersions(roleId);
  }

  async function savePermissions(roleId: string, payload: roleApi.SavePermissionsPayload): Promise<number> {
    const result = await roleApi.saveRolePermissions(roleId, payload);
    await loadVersions(roleId);
    return result.permVersion;
  }

  async function rollback(versionId: string, reason: string, roleId: string): Promise<number> {
    const result = await roleApi.rollbackPermissionVersion(versionId, reason);
    await loadVersions(roleId);
    return result.permVersion;
  }

  return {
    roles,
    tree,
    versions,
    loading,
    granted,
    dataScope,
    endAllowed,
    loadStaticData,
    loadVersions,
    savePermissions,
    rollback,
  };
});

/** 把权限上下文注入 core，供 hasPerm / v-perm 使用（core 不依赖 store） */
export function wirePermissionContext(): void {
  configurePermissionContext(() => {
    const auth = useAuthStore();
    if (!auth.user) return null;
    return {
      permissions: auth.user.permissions,
      dataScope: auth.user.dataScope,
      endBinding: auth.user.endBinding,
      roleCode: auth.user.roleCode,
    };
  });
}
